import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { SubscriptionPlanInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpaceSubscriptionPlansColumns } from './SubscriptionPlansColumns';
import { AdSpaceSubscriptionPlansUpdateDialog } from './SubscriptionPlansUpdateDialog';

const AdSpaceSubscriptionPlansAdminList: React.FC = () => {
    const [plans, setPlans] = useState<SubscriptionPlanInterface[]>([]);
    const [plan, setPlan] = useState<SubscriptionPlanInterface | null>(null);
    const [planId, setPlanId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<SubscriptionPlanInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [planToUpdate, setPlanToUpdate] = useState<SubscriptionPlanInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all plans
    const handleGetAllPlans = async () => {
        setError(null);
        setPlan(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<SubscriptionPlanInterface[]>>('/subscriptionPlans');
            if (result.status && result.data) {
                setPlans(result.data);
            } else {
                setError(result.message || 'Failed to fetch subscription plans');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch subscription plans');
        }
    };

    // Fetch plan by id
    const handleGetPlanById = async () => {
        setError(null);
        setPlans([]);
        setSuccess(null);
        if (!planId) return setError('Please enter a Subscription Plan ID');
        try {
            const result = await apiGet<GenericResponseInterface<SubscriptionPlanInterface>>(`/subscriptionPlans/${planId}`);
            if (result.status && result.data) {
                setPlan(result.data);
            } else {
                setError(result.message || 'Failed to fetch subscription plan');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch subscription plan');
        }
    };

    // Delete plan
    const handleDeletePlan = async () => {
        if (!planToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/subscriptionPlans/delete/${planToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Subscription plan deleted successfully');
                setDeleteDialogOpen(false);
                setPlanToDelete(null);
                handleGetAllPlans();
            } else {
                setError(result.message || 'Failed to delete subscription plan');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete subscription plan');
        }
    };

    // Update plan
    const handleUpdatePlan = async () => {
        if (!planToUpdate || !planToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<SubscriptionPlanInterface>>(
                `/subscriptionPlans/update/${planToUpdate.id}`,
                'PUT',
                planToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Subscription plan updated successfully');
                setUpdateDialogOpen(false);
                setPlanToUpdate(null);
                handleGetAllPlans();
            } else {
                setUpdateError(result.message || 'Failed to update subscription plan');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update subscription plan');
        }
    };

    const columns = getAdSpaceSubscriptionPlansColumns(
        setPlanToDelete,
        setDeleteDialogOpen,
        setPlanToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllPlans();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllPlans}>Get All Subscription Plans</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetPlanById}>Get Plan by ID</Button>
                <TextField
                    label="Subscription Plan ID"
                    value={planId}
                    onChange={e => setPlanId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {plans.length > 0 && (
                    <DataGrid
                        rows={plans.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {plan && (
                    <DataGrid
                        rows={[{ ...plan, id: plan.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this subscription plan?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeletePlan}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpaceSubscriptionPlansUpdateDialog
                open={updateDialogOpen}
                planToUpdate={planToUpdate}
                setPlanToUpdate={setPlanToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdatePlan}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpaceSubscriptionPlansAdminList;